import React, { memo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Dropdown, SlideUp, Text } from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import { IOptionDropdownProps } from './types'
import { useEventListener } from 'hooks'
import { useScrollableElement } from '../../../context'
import DropdownContent from './DropdownContent'

const OptionDropdown: React.FC<IOptionDropdownProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  disabled = false,
  className
}) => {
  const { scrollableElementRef } = useScrollableElement()
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(true)),
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(false))
  )

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const handleSelect = useCallback(
    (value: string | number) => {
      onSelect(value)
      closeDropdown()
    },
    [onSelect, closeDropdown]
  )

  useEventListener(
    'scroll',
    closeDropdown,
    !isTablet ? scrollableElementRef.current : null,
    true
  )

  const selectedOption = options.find(option => option.value === selectedValue)
  const displayValue = selectedOption?.label || label

  if (disabled || options.length === 0) {
    return (
      <Text type="h2" color="secondary" style={{ opacity: 0.5 }}>
        {displayValue}
      </Text>
    )
  }

  if (isTablet && isOpen) {
    return createPortal(
      <SlideUp isOpen={isOpen} onClose={closeDropdown}>
        <DropdownContent
          options={options}
          selectedValue={selectedValue}
          onSelect={handleSelect}
        />
      </SlideUp>,
      document.body
    )
  }

  return (
    <Dropdown
      className={className}
      headerContent={
        <Text type="h2" color="secondaryDark">
          {displayValue}
        </Text>
      }
      mainContent={
        <DropdownContent
          options={options}
          selectedValue={selectedValue}
          onSelect={handleSelect}
        />
      }
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
    />
  )
}

export default memo(OptionDropdown)
